import { formatDate, formatDateWithTime, formatTime } from '@longy/common'
import { Skeleton } from '@longy/web-components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { BookingStep } from '../../components/booking/BookingStep'
import { EventTicketQr } from '../../components/bookings/EventTicketQr'
import { api } from '../../core/trpc'
import { useLocale } from '../../hooks/useLocale'
import {
    BOOKING_BADGE_CLASSES,
    resolveBookingStatusBadge,
    resolveEventBookingStatus,
} from '../../utils/bookingList'
import { formatPrice } from '../../utils/formatPrice'

const PATH = '/bookings/events/$bookingId'

const DetailRow = ({ label, value }: { label: string; value: string }) => (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-2 last:border-0">
        <span className="text-brand-gray">{label}</span>
        <span className="text-right font-semibold">{value}</span>
    </div>
)

const formatEventTime = (startDate: Date, endDate: Date): string =>
    formatDate(startDate) === formatDate(endDate)
        ? `${formatDateWithTime(startDate)} – ${formatTime(endDate)}`
        : `${formatDateWithTime(startDate)} – ${formatDateWithTime(endDate)}`

const EventBookingDetailsPage = () => {
    const { t } = useTranslation()
    const { bookingId } = Route.useParams()
    const { getLocalizedValue } = useLocale()

    const { data: booking, isPending } = api.eventTicket.getMyBooking.useQuery(
        { id: bookingId },
        { retry: false },
    )

    if (isPending) {
        return (
            <BookingStep
                title={t('frontend.bookings.details')}
                step={t('frontend.auth.myBookings')}
            >
                <Skeleton className="h-80 w-full" />
            </BookingStep>
        )
    }

    if (!booking) {
        return (
            <BookingStep
                title={t('frontend.bookings.details')}
                step={t('frontend.auth.myBookings')}
            >
                <Link className="text-primary underline" to="/bookings">
                    {t('frontend.booking.backToBookings')}
                </Link>
            </BookingStep>
        )
    }

    const badge = resolveBookingStatusBadge(resolveEventBookingStatus(booking))
    const eventName = getLocalizedValue(booking.eventName) || t('frontend.bookings.event')
    const location = booking.locationName ? getLocalizedValue(booking.locationName) : ''

    return (
        <BookingStep title={t('frontend.bookings.details')} step={t('frontend.auth.myBookings')}>
            <div className="flex flex-col gap-6">
                <span
                    className={`self-start rounded-full px-3 py-1 text-xs font-semibold ${BOOKING_BADGE_CLASSES[badge.variant]}`}
                >
                    {t(badge.labelKey)}
                </span>

                <section className="rounded-lg border border-slate-200 bg-white p-6">
                    <DetailRow label={t('frontend.bookings.event')} value={eventName} />
                    <DetailRow
                        label={t('frontend.booking.dateAndTime')}
                        value={formatEventTime(booking.startDate, booking.endDate)}
                    />
                    {location && (
                        <DetailRow label={t('frontend.booking.location')} value={location} />
                    )}
                    {booking.ticketType && (
                        <DetailRow
                            label={t('frontend.bookings.ticketType')}
                            value={booking.ticketType}
                        />
                    )}
                    <DetailRow
                        label={t('frontend.bookings.ticketCount')}
                        value={String(booking.ticketCount)}
                    />
                    {booking.pricePaid !== null && (
                        <DetailRow
                            label={t('frontend.bookings.pricePaid')}
                            value={
                                booking.pricePaid === 0
                                    ? t('frontend.bookings.free')
                                    : formatPrice(booking.pricePaid)
                            }
                        />
                    )}
                </section>

                <section className="flex flex-col gap-4">
                    <h2 className="text-lg font-bold">{t('frontend.bookings.tickets')}</h2>
                    {booking.cancelled ? (
                        <p className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
                            {t('frontend.bookings.ticketsCancelled')}
                        </p>
                    ) : booking.tickets.length === 0 ? (
                        <p className="rounded-lg bg-slate-50 p-4 text-sm text-brand-gray">
                            {t('frontend.bookings.ticketsPending')}
                        </p>
                    ) : (
                        <div className="grid gap-4 md:grid-cols-2">
                            {booking.tickets.map((ticket, index) => (
                                <EventTicketQr
                                    key={ticket.code}
                                    code={ticket.code}
                                    holderName={ticket.holderName}
                                    ticketType={booking.ticketType}
                                    checkedInAt={ticket.checkedInAt}
                                    label={t('frontend.bookings.ticketOf', {
                                        index: index + 1,
                                        total: booking.tickets.length,
                                    })}
                                />
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </BookingStep>
    )
}

export const Route = createFileRoute(PATH)({
    head: () => ({ meta: [{ title: 'Long-Y - Foglalásaim' }] }),
    component: EventBookingDetailsPage,
})
